import { useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Button,
  MessageBar,
  MessageBarActions,
  MessageBarBody,
  MessageBarTitle,
  Skeleton,
  SkeletonItem,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Text,
  ToggleButton,
  Tooltip,
  mergeClasses,
} from '@coreai/fluentui-react';
import { ArrowClockwiseRegular, EyeRegular, EyeOffRegular, HistoryRegular } from '@fluentui/react-icons';
import { useRevisions } from '@/hooks/useApplicationManagement';
import { useTelemetry } from '@/hooks/useTelemetry';
import type { Revision } from '@/types/automation';
import { formatDateTime } from '@/utils/dateTime';
import { useRevisionsTabStyles } from './RevisionsTab.styles';

export interface RevisionsTabProps {
  projectName: string;
  appName: string;
}

type HealthTone = 'healthy' | 'unhealthy' | 'none';

const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error && error.message) {
    return error.message;
  }

  return 'An unexpected error occurred while loading revisions.';
};

const getHealthTone = (healthState: string): HealthTone => {
  switch (healthState.toLowerCase()) {
    case 'healthy':
      return 'healthy';
    case 'unhealthy':
      return 'unhealthy';
    default:
      return 'none';
  }
};

const getHealthBadgeClassName = (styles: ReturnType<typeof useRevisionsTabStyles>, healthState: string): string => {
  switch (getHealthTone(healthState)) {
    case 'healthy':
      return styles.healthBadgeHealthy;
    case 'unhealthy':
      return styles.healthBadgeUnhealthy;
    default:
      return styles.healthBadgeNone;
  }
};

const renderSkeletonRows = (styles: ReturnType<typeof useRevisionsTabStyles>) => (
  <TableBody className={styles.loadingState}>
    {Array.from({ length: 5 }).map((_, index) => (
      <TableRow key={`revision-skeleton-${index}`} aria-label="Loading revisions..." className={styles.row}>
        <TableCell className={styles.skeletonCell}>
          <Skeleton>
            <SkeletonItem className={mergeClasses(styles.skeletonItem, styles.skeletonName)} />
          </Skeleton>
        </TableCell>
        <TableCell className={styles.skeletonCell}>
          <Skeleton>
            <SkeletonItem className={mergeClasses(styles.skeletonItem, styles.skeletonBadge)} />
          </Skeleton>
        </TableCell>
        <TableCell className={styles.skeletonCell}>
          <Skeleton>
            <SkeletonItem className={mergeClasses(styles.skeletonItem, styles.skeletonDate)} />
          </Skeleton>
        </TableCell>
        <TableCell className={styles.skeletonCell}>
          <Skeleton>
            <SkeletonItem className={mergeClasses(styles.skeletonItem, styles.skeletonVersion)} />
          </Skeleton>
        </TableCell>
        <TableCell className={styles.skeletonCell}>
          <Skeleton>
            <SkeletonItem className={mergeClasses(styles.skeletonItem, styles.skeletonTraffic)} />
          </Skeleton>
        </TableCell>
        <TableCell className={styles.skeletonCell}>
          <Skeleton>
            <SkeletonItem className={mergeClasses(styles.skeletonItem, styles.skeletonReplicas)} />
          </Skeleton>
        </TableCell>
        <TableCell className={styles.skeletonCell}>
          <Skeleton>
            <SkeletonItem className={mergeClasses(styles.skeletonItem, styles.skeletonHealth)} />
          </Skeleton>
        </TableCell>
      </TableRow>
    ))}
  </TableBody>
);

const renderRevisionRow = (revision: Revision, styles: ReturnType<typeof useRevisionsTabStyles>) => {
  const rowClassName = mergeClasses(styles.row, revision.active ? styles.activeRow : undefined);
  const healthTone = getHealthTone(revision.healthState);

  // Cell shows browser-local time matching other "Created" columns (AppListPage,
  // WorkflowListPage, ApplicationStatusTab). Tooltip exposes the timezone label
  // on hover so users can confirm what zone the displayed value is in.
  const createdTimeText = formatDateTime(revision.createdTime, 'full', {
    fallback: revision.createdTime,
  });
  const createdTimeTooltip = formatDateTime(revision.createdTime, 'full', {
    fallback: revision.createdTime,
    seconds: true,
    timeZoneName: 'short',
  });

  return (
    <TableRow
      key={revision.name}
      className={rowClassName}
      aria-current={revision.active ? 'true' : undefined}
      data-active-revision={revision.active ? 'true' : 'false'}
    >
      <TableCell className={mergeClasses(styles.cell, styles.nameCell)}>
        <Tooltip content={revision.name} relationship="description">
          <span className={styles.nameText} tabIndex={0}>{revision.name}</span>
        </Tooltip>
      </TableCell>
      <TableCell className={styles.cell}>
        <Badge
          appearance="filled"
          shape="rounded"
          size="small"
          className={mergeClasses(styles.badgeBase, revision.active ? styles.activeBadge : styles.inactiveBadge)}
        >
          {revision.active ? 'Active' : 'Inactive'}
        </Badge>
      </TableCell>
      <TableCell className={styles.cell}>
        <Tooltip content={createdTimeTooltip} relationship="description">
          <span className={styles.createdText} tabIndex={0}>{createdTimeText}</span>
        </Tooltip>
      </TableCell>
      <TableCell className={mergeClasses(styles.cell, styles.nameCell)}>
        <Tooltip content={revision.image} relationship="description">
          <span className={styles.nameText} tabIndex={0}>{revision.image}</span>
        </Tooltip>
      </TableCell>
      <TableCell className={styles.cell}>
        <span className={styles.trafficText}>{`${revision.trafficWeight}%`}</span>
      </TableCell>
      <TableCell className={styles.cell}>
        <span className={styles.replicasText}>{`${revision.replicas?.running ?? 0}/${revision.replicas?.ready ?? 0}`}</span>
      </TableCell>
      <TableCell className={styles.cell}>
        <Badge
          appearance="filled"
          shape="rounded"
          size="small"
          className={mergeClasses(styles.badgeBase, getHealthBadgeClassName(styles, revision.healthState))}
          data-health-tone={healthTone}
        >
          {revision.healthState}
        </Badge>
      </TableCell>
    </TableRow>
  );
};

export const RevisionsTab = ({ projectName, appName }: RevisionsTabProps) => {
  const styles = useRevisionsTabStyles();
  const { trackEvent } = useTelemetry();
  const { data, isLoading, isFetching, isError, error, refetch } = useRevisions(projectName, appName);

  const [showZeroReplicas, setShowZeroReplicas] = useState(false);

  useEffect(() => {
    trackEvent('settings.revisions.viewed');
  }, [trackEvent]);

  // Sort by createdTime descending (most recent first), then filter
  const sortedRevisions = useMemo(() => {
    const all = data ?? [];
    const sorted = [...all].sort((a, b) => {
      const ta = new Date(a.createdTime).getTime();
      const tb = new Date(b.createdTime).getTime();
      return tb - ta;
    });
    return sorted;
  }, [data]);

  const revisions = useMemo(() => {
    if (showZeroReplicas) return sortedRevisions;
    return sortedRevisions.filter((r) => (r.replicas?.running ?? 0) > 0 || (r.replicas?.ready ?? 0) > 0);
  }, [sortedRevisions, showZeroReplicas]);

  const hiddenCount = sortedRevisions.length - revisions.length;
  const countLabel = `${revisions.length} revision${revisions.length === 1 ? '' : 's'}${hiddenCount > 0 ? ` (${hiddenCount} hidden)` : ''}`;
  const hasData = data !== undefined;

  const handleRefresh = () => {
    trackEvent('settings.revisions.refreshed');
    void refetch();
  };

  if (isError && !hasData) {
    return (
      <div className={styles.root}>
        <div className={styles.toolbar}>
          <Button icon={<ArrowClockwiseRegular />} onClick={handleRefresh}>
            Refresh
          </Button>
          <Text size={300} className={styles.countText}>
            Unable to load revisions
          </Text>
        </div>
        <div role="alert">
          <MessageBar intent="error" layout="multiline" className={styles.errorBar}>
            <MessageBarBody className={styles.errorBody}>
              <MessageBarTitle>Failed to load revisions</MessageBarTitle>
              {getErrorMessage(error)}
            </MessageBarBody>
            <MessageBarActions>
              <Button appearance="primary" onClick={handleRefresh}>
                Retry
              </Button>
            </MessageBarActions>
          </MessageBar>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <div className={styles.toolbar}>
        <ToggleButton
          checked={showZeroReplicas}
          onClick={() => setShowZeroReplicas((prev) => !prev)}
          icon={showZeroReplicas ? <EyeRegular /> : <EyeOffRegular />}
          appearance="subtle"
          size="small"
        >
          {showZeroReplicas ? 'Showing all revisions' : 'Hiding zero-replica revisions'}
        </ToggleButton>
        <Text size={300} className={styles.countText}>
          {isLoading && !hasData ? 'Loading revisions...' : countLabel}
        </Text>
        <div className={styles.toolbarRight}>
          <Tooltip content="Refresh revisions" relationship="label">
            <Button
              icon={isFetching ? <Spinner size="tiny" /> : <ArrowClockwiseRegular />}
              appearance="subtle"
              onClick={handleRefresh}
              disabled={isFetching}
              aria-label="Refresh revisions"
            />
          </Tooltip>
        </div>
      </div>

      {isError ? (
        <div role="alert">
          <MessageBar intent="error" layout="multiline" className={styles.errorBar}>
            <MessageBarBody className={styles.errorBody}>
              <MessageBarTitle>Failed to refresh revisions</MessageBarTitle>
              {getErrorMessage(error)}
            </MessageBarBody>
            <MessageBarActions>
              <Button appearance="primary" onClick={handleRefresh}>
                Retry
              </Button>
          </MessageBarActions>
          </MessageBar>
        </div>
      ) : null}

      <div className={styles.tableWrapper}>
        {isLoading && !hasData ? (
          <Table className={styles.table} aria-label="Revisions" size="medium" aria-busy>
            <TableHeader>
              <TableRow className={styles.headerRow}>
                <TableHeaderCell className={styles.headerCell}>Name</TableHeaderCell>
                <TableHeaderCell className={styles.headerCell}>Active</TableHeaderCell>
                <TableHeaderCell className={styles.headerCell}>Created</TableHeaderCell>
                <TableHeaderCell className={styles.headerCell}>Image</TableHeaderCell>
                <TableHeaderCell className={styles.headerCell}>Traffic</TableHeaderCell>
                <TableHeaderCell className={styles.headerCell}>Replicas</TableHeaderCell>
                <TableHeaderCell className={styles.headerCell}>Health</TableHeaderCell>
              </TableRow>
            </TableHeader>
            {renderSkeletonRows(styles)}
          </Table>
        ) : revisions.length === 0 ? (
          <div className={styles.emptyState} role="region" aria-label="No revisions found">
            <HistoryRegular className={styles.emptyIcon} aria-hidden="true" />
            <Text as="h3" size={500} weight="semibold">
              No revisions found
            </Text>
            <Text size={300}>This app does not have any Container App revisions to display.</Text>
          </div>
        ) : (
          <Table className={styles.table} aria-label="Revisions" size="medium" aria-busy={isFetching || undefined}>
            <TableHeader>
              <TableRow className={styles.headerRow}>
                <TableHeaderCell className={styles.headerCell}>Name</TableHeaderCell>
                <TableHeaderCell className={styles.headerCell}>Active</TableHeaderCell>
                <TableHeaderCell className={styles.headerCell}>Created</TableHeaderCell>
                <TableHeaderCell className={styles.headerCell}>Image</TableHeaderCell>
                <TableHeaderCell className={styles.headerCell}>Traffic</TableHeaderCell>
                <TableHeaderCell className={styles.headerCell}>Replicas</TableHeaderCell>
                <TableHeaderCell className={styles.headerCell}>Health</TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {revisions.map((revision) => renderRevisionRow(revision, styles))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
};
